import { FileExplorerBreadcrumb } from "@app/components/file_explorer/FileExplorerBreadcrumb";
import { FileExplorerContent } from "@app/components/file_explorer/FileExplorerContent";
import { canMoveFileToParentFolder } from "@app/components/file_explorer/fileExplorerDragDrop";
import { FileExplorerFilters } from "@app/components/file_explorer/FileExplorerFilters";
import type { ViewMode } from "@app/components/file_explorer/FileExplorerItem";
import { getFileExplorerPipeline } from "@app/components/file_explorer/fileExplorerPipeline";
import { FileExplorerToolbar } from "@app/components/file_explorer/FileExplorerToolbar";
import { MoveFileToFolderDialog } from "@app/components/file_explorer/MoveFileToFolderDialog";
import type {
  ContentNodeEntry,
  FileEntry,
  FileEntryWithId,
  FileExplorerDownloadEntry,
  FileExplorerEntry,
  FileExplorerFilter,
  FileExplorerMenuAction,
  FileExplorerPathEntry,
  FileExplorerVirtualScopeRoot,
  FileSystemTreeNode,
  FolderEntry,
  FramePackageEntry,
} from "@app/components/file_explorer/types";
import {
  buildFolderTree,
  countFoldersInTree,
  getFolderBreadcrumbSegments,
  getScopedRelativePath,
  isFileExplorerMovableFile,
} from "@app/components/file_explorer/utils";
import type { FileExplorerScopedPreferences } from "@app/hooks/useScopedUIPreferences";
import { useScopedPodUiPreferences } from "@app/hooks/useScopedUIPreferences";
import { openSourceUrl } from "@app/lib/utils/source_urls";
import { isInteractiveContentType } from "@app/types/files";
import type { Result } from "@app/types/shared/result";
import { Err } from "@app/types/shared/result";
import { cn, Edit04, FolderOpen, Trash01 } from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import type React from "react";
import { useCallback, useMemo, useState } from "react";

interface FileExplorerProps {
  contentClassName?: string;
  contentNodes?: ContentNodeEntry[];
  defaultViewMode?: ViewMode;
  emptyState?: React.ReactNode;
  hideBreadcrumbAtRoot?: boolean;
  currentFolderPath: string;
  files: FileExplorerPathEntry[];
  toolbarExtraActions?: React.ReactNode;
  isLoading: boolean;
  onCurrentFolderChange: (relativePath: string) => void;
  onDelete?: (entry: FileExplorerEntry) => Promise<void>;
  /** Restricts which entries get a Delete item when `onDelete` is set; all of them by default. */
  canDelete?: (entry: FileExplorerEntry) => boolean;
  onDownload: (entry: FileExplorerDownloadEntry) => Promise<void>;
  onMoveFile?: (
    entry: FileEntry,
    parentRelativePath: string
  ) => Promise<Result<void, Error>>;
  onOpenInteractive?: (entry: FileEntryWithId | FramePackageEntry) => void;
  onOpenInPanel: (entry: FileEntry) => void;
  onRename?: (entry: FileEntry | FolderEntry | FramePackageEntry) => void;
  getExtraFileMenuItems?: (
    entry: FileExplorerEntry
  ) => FileExplorerMenuAction[];
  /** Top-level scope folders at the virtual root (e.g. `conversation`, `pod`). */
  virtualScopeRoots?: readonly FileExplorerVirtualScopeRoot[];
}

export function FileExplorer({
  contentClassName,
  contentNodes = [],
  defaultViewMode = "grid",
  emptyState,
  currentFolderPath,
  files,
  toolbarExtraActions,
  hideBreadcrumbAtRoot = false,
  isLoading,
  onCurrentFolderChange,
  onDelete,
  canDelete,
  onDownload,
  onMoveFile,
  onOpenInteractive,
  onOpenInPanel,
  onRename,
  getExtraFileMenuItems,
  virtualScopeRoots,
}: FileExplorerProps) {
  const { t } = useLingui();
  const defaultPreferences = useMemo<FileExplorerScopedPreferences>(
    () => ({ viewMode: defaultViewMode, sortMode: "last-modified" }),
    [defaultViewMode]
  );
  const { value: preferences, setValue: setPreferences } =
    useScopedPodUiPreferences({
      scope: "fileExplorer",
      // One layout and sort preference for the whole app, not per workspace or pod.
      resourceId: "global",
      defaultValue: defaultPreferences,
    });
  const { viewMode, sortMode } = preferences;
  const setViewMode = useCallback(
    (viewMode: ViewMode) => setPreferences({ ...preferences, viewMode }),
    [preferences, setPreferences]
  );
  const setSortMode = useCallback(
    (sortMode: FileExplorerScopedPreferences["sortMode"]) =>
      setPreferences({ ...preferences, sortMode }),
    [preferences, setPreferences]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FileExplorerFilter>("all");
  // Flat listings (search or a type filter) show explorer-relative paths as titles.
  const searchFolderPath =
    searchQuery.trim() || activeFilter !== "all"
      ? currentFolderPath
      : undefined;
  const [fileToMove, setFileToMove] = useState<FileEntry | null>(null);
  const [showMoveDialog, setShowMoveDialog] = useState(false);

  const {
    sortedNodes,
    filterCounts,
    folderCount,
    fileCount,
    entryByRelativePath,
  } = useMemo(
    () =>
      getFileExplorerPipeline({
        contentNodes,
        currentFolderPath,
        files,
        searchQuery,
        activeFilter,
        sortMode,
        virtualScopeRoots,
      }),
    [
      contentNodes,
      currentFolderPath,
      files,
      searchQuery,
      activeFilter,
      sortMode,
      virtualScopeRoots,
    ]
  );

  const folderTree = useMemo(() => buildFolderTree(files), [files]);
  const totalFolderCount = useMemo(
    () => countFoldersInTree(folderTree),
    [folderTree]
  );

  const getMenuItems = useCallback(
    (entry: FileExplorerEntry): FileExplorerMenuAction[] => {
      const items: FileExplorerMenuAction[] =
        getExtraFileMenuItems?.(entry) ?? [];
      if (entry.kind === "frame_package") {
        items.push({
          label: t`View source`,
          icon: FolderOpen,
          onClick: (e) => {
            e.stopPropagation();
            onCurrentFolderChange(entry.sourceFolderPath);
            setActiveFilter("all");
          },
        });
        if (onRename) {
          items.push({
            label: t`Rename`,
            icon: Edit04,
            onClick: (e) => {
              e.stopPropagation();
              onRename(entry);
            },
          });
        }
        if (onDelete && (canDelete?.(entry) ?? true)) {
          items.push({
            label: t`Delete`,
            icon: Trash01,
            variant: "warning",
            onClick: (e) => {
              e.stopPropagation();
              void onDelete(entry);
            },
          });
        }
        return items;
      }
      if (onRename && (entry.kind === "file" || entry.kind === "folder")) {
        items.push({
          label: t`Rename`,
          icon: Edit04,
          onClick: (e) => {
            e.stopPropagation();
            onRename(entry);
          },
        });
      }
      if (
        onMoveFile &&
        totalFolderCount > 0 &&
        entry.kind === "file" &&
        isFileExplorerMovableFile(entry)
      ) {
        items.push({
          label: t`Move to…`,
          icon: FolderOpen,
          onClick: (e) => {
            e.stopPropagation();
            setFileToMove(entry);
            setShowMoveDialog(true);
          },
        });
      }
      if (onDelete && (canDelete?.(entry) ?? true)) {
        items.push({
          label: entry.kind === "node" ? t`Remove` : t`Delete`,
          icon: Trash01,
          variant: "warning",
          onClick: (e) => {
            e.stopPropagation();
            void onDelete(entry);
          },
        });
      }
      return items;
    },
    [
      canDelete,
      getExtraFileMenuItems,
      onCurrentFolderChange,
      onDelete,
      onMoveFile,
      onRename,
      t,
      totalFolderCount,
    ]
  );

  const handleMoveToFolder = useCallback(
    async (parentRelativePath: string) => {
      if (!fileToMove || !onMoveFile) {
        return new Err(new Error("No file selected to move."));
      }
      return onMoveFile(fileToMove, parentRelativePath);
    },
    [fileToMove, onMoveFile]
  );

  const handleBreadcrumbNavigate = (index: number) => {
    if (index < 0) {
      onCurrentFolderChange("");
      setActiveFilter("all");
      return;
    }

    const segments = getFolderBreadcrumbSegments(currentFolderPath);
    onCurrentFolderChange(segments[index]?.path ?? "");
    setActiveFilter("all");
  };

  const handleFolderNavigate = (node: FileSystemTreeNode) => {
    onCurrentFolderChange(node.path);
    setActiveFilter("all");
  };

  const fileDragEnabled = Boolean(onMoveFile && totalFolderCount > 0);

  const handleMoveFileDrop = useCallback(
    async (scopedFilePath: string, parentRelativePath: string) => {
      if (
        !onMoveFile ||
        !canMoveFileToParentFolder(scopedFilePath, parentRelativePath)
      ) {
        return;
      }

      const relativePath = getScopedRelativePath(scopedFilePath);
      const entry = entryByRelativePath.get(relativePath);
      if (entry?.kind !== "file" || !isFileExplorerMovableFile(entry)) {
        return;
      }

      await onMoveFile(entry, parentRelativePath);
    },
    [entryByRelativePath, onMoveFile]
  );

  const handleFileOpen = (entry: FileEntry) => {
    if (
      onOpenInteractive &&
      isInteractiveContentType(entry.contentType) &&
      entry.fileId
    ) {
      onOpenInteractive({ ...entry, fileId: entry.fileId });
      return;
    }
    onOpenInPanel(entry);
  };

  const handleFramePackageOpen = (entry: FramePackageEntry) => {
    onOpenInteractive?.(entry);
  };

  const handleNodeOpen = (entry: ContentNodeEntry) => {
    openSourceUrl(entry.sourceUrl);
  };

  const showBreadcrumb = !(hideBreadcrumbAtRoot && currentFolderPath === "");

  return (
    <>
      <div className="flex h-full w-full min-h-0 flex-1 flex-col">
        <div
          className={cn("flex flex-1 min-h-0 flex-col gap-5", contentClassName)}
        >
          {showBreadcrumb && (
            <div className="px-4 pt-5">
              <FileExplorerBreadcrumb
                currentFolderPath={currentFolderPath}
                onNavigate={handleBreadcrumbNavigate}
                onMoveFileDrop={
                  fileDragEnabled ? handleMoveFileDrop : undefined
                }
              />
            </div>
          )}
          <div
            className={cn(
              "px-4",
              hideBreadcrumbAtRoot && !showBreadcrumb && "pt-5"
            )}
          >
            <FileExplorerToolbar
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortMode={sortMode}
              onSortModeChange={setSortMode}
              toolbarExtraActions={toolbarExtraActions}
            />
          </div>
          {Object.keys(filterCounts).length > 1 && (
            <div className="px-4">
              <FileExplorerFilters
                active={activeFilter}
                onActiveChange={setActiveFilter}
                counts={filterCounts}
              />
            </div>
          )}
          <FileExplorerContent
            isLoading={isLoading}
            sortedNodes={sortedNodes}
            entryByRelativePath={entryByRelativePath}
            viewMode={viewMode}
            isEmpty={fileCount === 0 && folderCount === 0}
            emptyState={emptyState}
            fileDragEnabled={fileDragEnabled}
            onFolderNavigate={handleFolderNavigate}
            onFileOpen={handleFileOpen}
            onFramePackageOpen={handleFramePackageOpen}
            onDownload={onDownload}
            onMoveFileDrop={fileDragEnabled ? handleMoveFileDrop : undefined}
            onNodeOpen={handleNodeOpen}
            getFileMenuItems={getMenuItems}
            searchFolderPath={searchFolderPath}
          />
        </div>
      </div>

      {onMoveFile && (
        <MoveFileToFolderDialog
          folderTree={folderTree}
          file={fileToMove}
          isOpen={showMoveDialog}
          onClose={() => setShowMoveDialog(false)}
          onMove={handleMoveToFolder}
        />
      )}
    </>
  );
}
